/**
 * Datalähde-komponenttien renderöintiavut (AB-WP4.1). Puhtaita funktioita
 * (testattavissa ilman DOM-bootia): sarakepolun resolvointi, vastausrungon
 * rivien poiminta ja dynamic-table-tbodyn täyttö. Verkkokutsu itse tehdään
 * runtime-sillan ({@link ./bridge}) kautta; nämä vain muuntavat vastauksen
 * DOM:ksi.
 */

export interface TableColumn {
  header?: string;
  /** Dot-polku rivin objektiin (esim. "customer.name"). */
  path?: string;
  type?: string;
  format?: string;
}

/**
 * Yksi row-action-rungon kenttämäppäys (AB-WP4.1). Jokainen entry täyttää yhden
 * request-body-kentän ({@link field}) lähteestä riippuen:
 *   - `rowPath`  → poimi klikatulta riviltä dot-polulla ({@link path})
 *   - `static`   → vakioarvo ({@link value})
 *   - `binding`  → evaluoi reaktiivinen lauseke ({@link binding}) storen tilaa vasten
 */
export interface RowActionMappingEntry {
  field?: string;
  source?: 'rowPath' | 'static' | 'binding' | 'context';
  path?: string;
  value?: unknown;
  binding?: string;
}

/**
 * Per-rivi-toiminto dynamic-tablelle: nappi (label) ja valinnainen klikkauskäsittelijä.
 * Ilman {@link onClick}:ia nappi renderöityy disabloituna (esikatselu ilman siltaa,
 * tai toiminto jolla ei ole apiId:tä eikä kortteja).
 */
export interface RowAction {
  label: string;
  onClick?: (row: Record<string, unknown>, button: HTMLButtonElement) => void;
}

/**
 * Rivillä näkyvien toimintonappien enimmäismäärä (WP-KH4, päätös D5 = C).
 * Loput toiminnot ovat ⋯-napin avaamassa valikossa. Kirjoittajan katto (10)
 * on palvelimella (RowActionsProp.MAX_ROW_ACTIONS); tämä on vain piirtoasia.
 */
export const INLINE_ROW_ACTIONS = 3;

/**
 * Auki oleva ylivuotovalikko — kerrallaan yksi koko dokumentissa. Moduulitason
 * tila on tarkoituksellinen: valikko elää bodyssä eikä kuulu millekään
 * yhdelle taululle, ja uudelleenpiirto tuhoaa solun johon se viittaa.
 */
let openMenu: { close: () => void } | null = null;

/** Sulje auki oleva ylivuotovalikko (uudelleenpiirto, toinen valikko, Escape …). */
export function closeRowActionMenu(): void {
  const menu = openMenu;
  openMenu = null;
  menu?.close();
}

function buildActionButton(
  doc: Document,
  action: RowAction,
  row: Record<string, unknown>,
): HTMLButtonElement {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.className = 'sp-dt-action-btn';
  btn.textContent = action.label; // textContent → ei XSS labelista
  if (action.onClick) {
    const handler = action.onClick;
    btn.addEventListener('click', () => handler(row, btn));
  } else {
    btn.disabled = true;
  }
  return btn;
}

/**
 * ⋯-nappi ylivuototoiminnoille (D5 = C). Valikko rakennetaan avattaessa,
 * liitetään bodyyn ja sijoitetaan kiinteästi napin kohdalle: solun sisällä
 * se leikkautuisi modaalin overflow-y:hyn ja muihin vierittäviin kontteihin.
 * Sulkeutuu valintaan, Escapeen, ulkopuoliseen klikkaukseen, vieritykseen ja
 * uudelleenpiirtoon. Valikon toiminto saa napikseen ⋯:n — busy-tila ja
 * korttien host ovat siinä, ja valikko on jo kiinni kun käsittelijä ajaa.
 */
function buildOverflowButton(
  doc: Document,
  actions: RowAction[],
  row: Record<string, unknown>,
): HTMLButtonElement {
  const more = doc.createElement('button');
  more.type = 'button';
  more.className = 'sp-dt-action-btn sp-dt-action-more';
  more.textContent = '\u22ef';
  more.setAttribute('aria-label', 'More actions');
  more.setAttribute('aria-haspopup', 'menu');
  more.setAttribute('aria-expanded', 'false');
  more.addEventListener('click', () => {
    const wasOpen = more.getAttribute('aria-expanded') === 'true';
    closeRowActionMenu();
    if (wasOpen) return;

    const menu = doc.createElement('div');
    menu.className = 'sp-dt-action-menu';
    menu.setAttribute('role', 'menu');
    for (const action of actions) {
      const item = doc.createElement('button');
      item.type = 'button';
      item.className = 'sp-dt-action-menu-item';
      item.setAttribute('role', 'menuitem');
      item.textContent = action.label;
      if (action.onClick) {
        const handler = action.onClick;
        item.addEventListener('click', () => {
          closeRowActionMenu();
          handler(row, more);
        });
      } else {
        item.disabled = true;
      }
      menu.appendChild(item);
    }
    const rect = more.getBoundingClientRect();
    const viewportWidth = doc.defaultView?.innerWidth ?? 0;
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.right = `${Math.max(0, viewportWidth - rect.right)}px`;

    // Avaava klikki kuplii vielä dokumenttiin tämän kuuntelijan lisäämisen
    // jälkeen — se ohitetaan kohteen perusteella, ei ajastimella.
    const onDocClick = (e: Event) => {
      const target = e.target as Node | null;
      if (target && (menu.contains(target) || more.contains(target))) return;
      closeRowActionMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRowActionMenu();
    };
    const onScroll = () => closeRowActionMenu();
    doc.addEventListener('click', onDocClick);
    doc.addEventListener('keydown', onKey);
    doc.addEventListener('scroll', onScroll, true);
    doc.body.appendChild(menu);
    more.setAttribute('aria-expanded', 'true');
    openMenu = {
      close: () => {
        doc.removeEventListener('click', onDocClick);
        doc.removeEventListener('keydown', onKey);
        doc.removeEventListener('scroll', onScroll, true);
        menu.remove();
        more.setAttribute('aria-expanded', 'false');
      },
    };
  });
  return more;
}

/**
 * Yksi taulun request-parametri (AB-WP4.1). {@link in} päättää menkö parametri
 * URL:n query-osaan vai request-runkoon; {@link source} päättää arvon lähteen
 * (vakio {@link value} vai reaktiivinen lauseke {@link binding}). Binding-parametrit
 * uusivat haun kun viitattu slot muuttuu (runtime tilaa riippuvuudet).
 */
export interface RequestParameter {
  name?: string;
  in?: 'query' | 'body';
  source?: 'static' | 'binding';
  value?: unknown;
  binding?: string;
}

/** Yhtä hakua varten lasketut parametrit: query → URL, body → request-runko. */
export interface RequestParams {
  query: Record<string, unknown>;
  body: Record<string, unknown>;
}

/**
 * Laske request-parametrit yhtä hakua varten. Puhdas funktio: {@link resolveBinding}
 * annetaan ulkopuolelta (evaluoi lausekkeen storen tilaa vasten testattavasti).
 * Nimeämättömät entryt ohitetaan; {@link RequestParameter.in} oletuksena "query".
 */
export function buildRequestParams(
  params: RequestParameter[],
  resolveBinding: (expr: string) => unknown,
): RequestParams {
  const query: Record<string, unknown> = {};
  const body: Record<string, unknown> = {};
  for (const p of params) {
    if (!p || !p.name) continue;
    const value =
      p.source === 'binding' ? (p.binding ? resolveBinding(p.binding) : undefined) : p.value;
    (p.in === 'body' ? body : query)[p.name] = value;
  }
  return { query, body };
}

/**
 * Rakenna row-action-pyynnön runko mappingista yhtä klikattua riviä vasten.
 * Puhdas funktio (ei DOM/verkko): {@link resolveBinding} annetaan ulkopuolelta,
 * jotta binding-lähde evaluoituu storen tilaa vasten testattavasti. Kentät ilman
 * nimeä ({@link RowActionMappingEntry.field}) ohitetaan.
 */
export function buildRowActionBody(
  mapping: RowActionMappingEntry[],
  row: Record<string, unknown>,
  resolveBinding: (expr: string) => unknown,
  context?: Record<string, unknown>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const entry of mapping) {
    if (!entry || !entry.field) continue;
    let value: unknown;
    switch (entry.source) {
      case 'static':
        value = entry.value;
        break;
      case 'binding':
        value = entry.binding ? resolveBinding(entry.binding) : undefined;
        break;
      // WP-KL4: kanbanin siirto antaa oman kontekstinsa (cardId/fromColumnId/
      // toColumnId). Se on eri asia kuin `rowPath`, joka lukee siirretyn KORTIN
      // kenttiä — molempia tarvitaan samassa rungossa.
      case 'context':
        value = context ? resolvePath(context, entry.path) : undefined;
        break;
      case 'rowPath':
      default:
        value = resolvePath(row, entry.path);
        break;
    }
    body[entry.field] = value;
  }
  return body;
}

/** Resolvoi dot-polku objektista; puuttuva polku → undefined. */
export function resolvePath(row: unknown, path: string | undefined): unknown {
  if (!path) return undefined;
  let cur: unknown = row;
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/**
 * Poimi rivit proxatusta responseBody:sta. Tukee: top-level array, tai
 * objekti jonka rows/data/items on array. Muuten tyhjä.
 */
export function extractRows(responseBody: string): Record<string, unknown>[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(responseBody);
  } catch {
    return [];
  }
  if (Array.isArray(parsed)) return parsed as Record<string, unknown>[];
  if (parsed && typeof parsed === 'object') {
    for (const key of ['rows', 'data', 'items', 'results']) {
      const v = (parsed as Record<string, unknown>)[key];
      if (Array.isArray(v)) return v as Record<string, unknown>[];
    }
  }
  return [];
}

/**
 * Käyttäjälle näytettävän virheviestin pituuskatto (D3 = A). Toast on yksi
 * rivi; pidempi teksti on luettavissa `lastResponse`ista rajaamattomana.
 */
export const ERROR_MESSAGE_MAX = 300;

/**
 * Proxatun vastausrungon parsittu muoto: JSON-arvo, tai raaka merkkijono kun
 * runko ei ole JSONia. Appin oma envelope on näin luettavissa lausekkeella
 * silloinkin kun se ei noudata {@link extractErrorMessage}n konventiota.
 */
export function parseResponseBody(responseBody: string): unknown {
  try {
    return JSON.parse(responseBody);
  } catch {
    return responseBody;
  }
}

/**
 * Poimi virheviesti vastausrungosta — sama konventiokuvio kuin
 * {@link extractRows}. Koettaa järjestyksessä `error.message`, `error`
 * (merkkijonona), `message`, `detail`, `title`; ensimmäinen ei-tyhjä
 * merkkijono voittaa. Ei-JSON-runko → `''`, jottei upstreamin HTML-virhesivu
 * päädy ruudulle. Tulos katkaistaan {@link ERROR_MESSAGE_MAX}iin.
 */
export function extractErrorMessage(responseBody: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(responseBody);
  } catch {
    return '';
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return '';
  const obj = parsed as Record<string, unknown>;
  const err = obj.error;
  const candidates = [
    err && typeof err === 'object' ? (err as Record<string, unknown>).message : undefined,
    err,
    obj.message,
    obj.detail,
    obj.title,
  ];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    const text = c.trim();
    if (!text) continue;
    return text.length > ERROR_MESSAGE_MAX
      ? `${text.slice(0, ERROR_MESSAGE_MAX - 1).trimEnd()}…`
      : text;
  }
  return '';
}

/** Muotoile soluarvo näytettäväksi merkkijonoksi. */
function formatCell(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Renderöi rivit dynamic-tablen tbodyyn. Palauttaa rivien määrän (status-
 * viestiä varten). Käyttää annetun dokumentin createElementiä (jsdom-turvallinen,
 * ei innerHTML → ei XSS soluarvoista). {@link rowActions}in ollessa epätyhjä
 * jokainen rivi saa toimintosolun: enintään {@link INLINE_ROW_ACTIONS} nappia
 * rivillä ja loput ⋯-valikossa (WP-KH4, D5 = C); {@link RowAction.onClick} saa
 * juuri sen rivin objektin sulkeumana. Tyhjä lista → ei toimintosaraketta.
 */
export function renderTableRows(
  tbody: HTMLElement,
  rows: Record<string, unknown>[],
  columns: TableColumn[],
  rowKeyPath: string,
  rowActions: RowAction[],
): number {
  const doc = tbody.ownerDocument;
  // Uudelleenpiirto tuhoaa solun johon auki oleva valikko viittaa.
  closeRowActionMenu();
  tbody.textContent = '';
  for (const row of rows) {
    const tr = doc.createElement('tr');
    const key = resolvePath(row, rowKeyPath);
    if (key != null) tr.setAttribute('data-app-row-id', String(key));
    for (const col of columns) {
      const td = doc.createElement('td');
      td.textContent = formatCell(resolvePath(row, col.path));
      tr.appendChild(td);
    }
    if (rowActions.length > 0) {
      const td = doc.createElement('td');
      td.className = 'sp-dt-action-cell';
      const overflow = rowActions.length > INLINE_ROW_ACTIONS;
      const inline = overflow ? rowActions.slice(0, INLINE_ROW_ACTIONS) : rowActions;
      for (const action of inline) td.appendChild(buildActionButton(doc, action, row));
      if (overflow) {
        td.appendChild(buildOverflowButton(doc, rowActions.slice(INLINE_ROW_ACTIONS), row));
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  return rows.length;
}
